import forge from 'node-forge';

const PUBLIC_KEY_PEM = `
-----BEGIN PUBLIC KEY-----
MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAqFRNqg77kTqeWJJfVkLt
ZJC3PSlFPt4NUHCVAFPMPGY6voFJzpSS1MjBTjaKLvjC6LWo95SXw2ENt2K1fnl9
kbd+YXs43ZXZflrI3i767+O+wH73KNNDqcPCW+qrB3aC4TaZN8u6HJV5Y/aAO6jB
nKsYqMvY7bUBnBaSlq84GvLHMIf1xyhCngM5lCADIXyK4q0N1ID5DWmOV6+CaBQF
T5JerOLozCYPJ95pUsaV7de5/O5IzvPokVScNJvkHELRqsp2z2afwHywo4ALIgAS
o+I3PyADYnzpmTXjZrRcQWTZdEY3AfDRcOsO3Z4JhSu1cM+8A1a5Gg8m37SxhYG3
mwIDAQAB
-----END PUBLIC KEY-----
`.trim();

const pemToArrayBuffer = (pem) => {
  const b64 = pem.replace(/-----.*-----/g, '').replace(/\s+/g, '');
  const binaryDerString = atob(b64);
  const binaryDer = new Uint8Array(binaryDerString.length);
  for (let i = 0; i < binaryDerString.length; i++) {
    binaryDer[i] = binaryDerString.charCodeAt(i);
  }
  return binaryDer.buffer;
};

const importRsaPublicKey = async (pem) => {
  const keyBuffer = pemToArrayBuffer(pem);
  return await crypto.subtle.importKey(
    'spki',
    keyBuffer,
    { name: 'RSA-OAEP', hash: { name: 'SHA-256' } },
    true, //
    ['encrypt']
  );
};

// Native Web Crypto path (fast, used by Chrome/Blink).
const encryptWithWebCrypto = async (text, publicKeyPem) => {
  const key = await importRsaPublicKey(publicKeyPem);
  const encoded = new TextEncoder().encode(text);
  const encrypted = await crypto.subtle.encrypt(
    { name: 'RSA-OAEP', hash: { name: 'SHA-256' } },
    key,
    encoded
  );
  return btoa(String.fromCharCode(...new Uint8Array(encrypted)));
};

// Pure-JS fallback (node-forge). Used when Web Crypto RSA-OAEP fails,
// e.g. WebKit/Orion which throws "OperationError" on crypto.subtle.encrypt.
const encryptWithForge = (text, publicKeyPem) => {
  const publicKey = forge.pki.publicKeyFromPem(publicKeyPem);
  const encrypted = publicKey.encrypt(forge.util.encodeUtf8(text), 'RSA-OAEP', {
    md: forge.md.sha256.create(),
    mgf1: { md: forge.md.sha256.create() }
  });
  return forge.util.encode64(encrypted);
};

export const encryptRSA = async (text, publicKeyPem = PUBLIC_KEY_PEM) => {
  try {
    if (typeof crypto !== 'undefined' && crypto.subtle) {
      return await encryptWithWebCrypto(text, publicKeyPem);
    }
  } catch (err) {
    // Fall through to the pure-JS implementation on WebKit/Orion failures.
    console.warn('Web Crypto RSA-OAEP failed, falling back to node-forge:', err);
  }
  return encryptWithForge(text, publicKeyPem);
};
